import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import * as _ from 'lodash';
import { ContentBox } from '../../../../shared/content-box/ContentBox';
import { SpinLoader } from '../../../../shared/spin-loader/SpinLoader';
import FriendlyDate from '../../../../shared/FriendlyDate';
import { TimeZoneNote } from '../../../../shared/time-zone-note/TimeZoneNote';
import TransparentButton from '../../../../shared/button/transparent-button/TransparentButton';
import { PageActionsRow } from '../../../../shared/headers/PageActionsRow';
import { BackButton } from '../../../../shared/button/back-button/BackButton';
import { ConfirmModal } from '../common/ConfirmModal';
import { RunStatusBadge } from '../common/RunStatusBadge';
import { RunProgress } from '../common/RunProgress';
import { RuleSetChanges } from '../common/RuleSetChanges';
import { StatTile } from '../common/StatTile';
import { ConnectionHint } from '../common/ConnectionHint';
import { BlueContainedButton } from '../../../../shared/button/blue-contained-button/BlueContainedButton';
import { RunResults } from '../results/RunResults';
import {
    cancelRun,
    deleteRun,
    fetchScenarios,
    followRun,
    stopFollowingRun
} from '../redux/runs/actions';
import { getRunState, getScenariosState } from '../redux/selectors';
import {
    getBrandsAndJurisdictions,
    getSelectedBrand
} from '../../../../jurisdiction/redux/selectors';
import {
    fetchJurisdictions,
    setJurisdiction
} from '../../../../jurisdiction/redux/actions';
import {
    redirectTo,
    redirectToNewRunFrom
} from '../../../../routing/redirectService';
import {
    Connection,
    findRunSelection,
    getRunRole,
    getScenarioName,
    isActiveStatus,
    isTerminalStatus,
    RunRole,
    RunStatus
} from '../simulation.model';
import { SimulationPath } from '../simulation.paths';
import { isComparisonEnabled } from '../../../../shared/feature-flags';

const RunAction = Object.freeze({ CANCEL: 'CANCEL', DELETE: 'DELETE' });

/* A variant of "Test a change" was played with changed rules, which a new run does not have: it cannot be played again as it was. */
const canRunAgain = run =>
    isTerminalStatus(run.status) && getRunRole(run) !== RunRole.VARIANT;

class RunDetailContainer extends Component {
    constructor(props) {
        super(props);
        this.state = { pending: null };
        this.confirmRef = React.createRef();
        this.refreshedJurisdictions = false;
    }

    get runId() {
        return this.props.match.params.runId;
    }

    componentDidMount() {
        this.props.fetchScenarios();
        this.props.followRun({ runId: this.runId });
    }

    componentDidUpdate(prevProps) {
        if (prevProps.match.params.runId !== this.runId) {
            this.refreshedJurisdictions = false;
            this.props.followRun({ runId: this.runId });
        }
        // The brand of a run is made when it starts, so the list that the selector at the top is made from may not have it yet
        if (
            this.props.run &&
            this.props.run.brandId &&
            !this.findBrand() &&
            !this.refreshedJurisdictions
        ) {
            this.refreshedJurisdictions = true;
            this.props.fetchJurisdictions();
        }
    }

    componentWillUnmount() {
        this.props.stopFollowingRun();
    }

    findBrand = () => {
        const { run, brandsAndJurisdictions } = this.props;
        const brands = _.get(brandsAndJurisdictions, 'brands', []);

        return (
            run &&
            _.find(brands, brand => String(brand.id) === String(run.brandId))
        );
    };

    /* The data of the run is the data of its brand: select it at the top, like for any other brand, and go to a screen with it. */
    viewIn = path => {
        const selection = findRunSelection(
            this.props.run,
            this.props.brandsAndJurisdictions
        );

        if (selection) {
            this.props.setJurisdiction(selection);
            redirectTo(path);
        }
    };

    /* A new run with the sandbox, the seed and the scenarios of this one. */
    runAgain = () => redirectToNewRunFrom(this.props.run.id);

    tryAgain = () => this.props.followRun({ runId: this.runId });

    ask = action =>
        this.setState({ pending: action }, () =>
            this.confirmRef.current.open()
        );

    confirmPending = () => {
        const { pending } = this.state;

        if (pending === RunAction.CANCEL) {
            this.props.cancelRun({ runId: this.runId });
        } else if (pending === RunAction.DELETE) {
            this.props.deleteRun({ runId: this.runId, redirect: true });
        }
    };

    describePending = () =>
        this.state.pending === RunAction.CANCEL
            ? {
                  title: `Cancel run #${this.runId}`,
                  confirmText: 'Cancel run',
                  message:
                      'The run is stopped. What it produced so far is kept.'
              }
            : {
                  title: `Delete run #${this.runId}`,
                  confirmText: 'Delete run',
                  message:
                      'The run and everything it produced are deleted. This cannot be undone.'
              };

    renderDetails(run) {
        const role = getRunRole(run);

        return (
            <Fragment>
                <dl className={'sim-dl'}>
                    <div>
                        <dt>Status</dt>
                        <dd>
                            <RunStatusBadge status={run.status} />
                            {role === RunRole.VARIANT && (
                                <span className={'sim-tag variant'}>
                                    variant
                                </span>
                            )}
                            {role === RunRole.BASELINE && (
                                <span className={'sim-tag'}>baseline</span>
                            )}
                        </dd>
                    </div>
                    <div>
                        <dt>Sandbox</dt>
                        <dd>{run.workspace}</dd>
                    </div>
                    {run.brandName && (
                        <div>
                            <dt>Brand</dt>
                            <dd>{run.brandName}</dd>
                        </div>
                    )}
                    <div>
                        <dt>Seed</dt>
                        <dd className={'sim-mono'}>{run.seed}</dd>
                    </div>
                    <div>
                        <dt>Started by</dt>
                        <dd>{_.get(run, 'createdBy.name')}</dd>
                    </div>
                    <div>
                        <dt>Created</dt>
                        <dd>
                            <FriendlyDate timestamp={run.createdAt} />
                        </dd>
                    </div>
                    <div>
                        <dt>Started</dt>
                        <dd>
                            {run.startedAt ? (
                                <FriendlyDate timestamp={run.startedAt} />
                            ) : (
                                '–'
                            )}
                        </dd>
                    </div>
                    <div>
                        <dt>Finished</dt>
                        <dd>
                            {run.finishedAt ? (
                                <FriendlyDate timestamp={run.finishedAt} />
                            ) : (
                                '–'
                            )}
                        </dd>
                    </div>
                    <div>
                        <dt>Scenarios</dt>
                        <dd>
                            {_.map(run.scenarios, id => (
                                <span key={id} className={'sim-tag'}>
                                    {getScenarioName(
                                        this.props.scenariosById,
                                        id
                                    )}
                                </span>
                            ))}
                        </dd>
                    </div>
                </dl>
                <TimeZoneNote />
            </Fragment>
        );
    }

    renderProgress(run) {
        const { progress } = run;

        return (
            <div>
                <RunProgress progress={progress} status={run.status} detailed />
                <div className={'sim-stats'} style={{ marginTop: 20 }}>
                    <StatTile
                        label={'Planned'}
                        value={progress.eventsPlanned}
                    />
                    <StatTile
                        label={'Generated'}
                        value={progress.eventsGenerated}
                    />
                    <StatTile
                        label={'Processed'}
                        value={progress.eventsProcessed}
                    />
                    <StatTile label={'Failed'} value={progress.eventsFailed} />
                </div>
            </div>
        );
    }

    render() {
        const { run, loading, notFound, results, scenariosById } = this.props;
        const description = this.describePending();

        if (notFound) {
            return (
                <div className={'sim-banner warning'}>
                    <i className={'material-icons'}>warning</i>
                    <div>
                        Run #{this.runId} does not exist (any more). It may have
                        been deleted.
                    </div>
                </div>
            );
        }
        if (!run) {
            // The first read may be what fails, when the backend is restarting: say so instead of showing nothing
            return (
                <div>
                    <ConnectionHint
                        connection={this.props.connection}
                        onRetry={this.tryAgain}
                    />
                    {loading && this.props.connection !== Connection.LOST && (
                        <SpinLoader />
                    )}
                </div>
            );
        }
        const brand = this.findBrand();
        const changes = _.get(run, 'ruleSet.changes');
        const interrupted = run.status === RunStatus.INTERRUPTED;

        return (
            <div className={'sim-run-detail'}>
                <PageActionsRow leftContent={<BackButton />}>
                    {brand && (
                        <TransparentButton
                            onClick={() =>
                                this.viewIn('/alerts/anti-money-laundering')
                            }
                        >
                            View alerts
                        </TransparentButton>
                    )}
                    {brand && (
                        <TransparentButton
                            onClick={() => this.viewIn('/players')}
                        >
                            View players
                        </TransparentButton>
                    )}
                    {brand && (
                        <TransparentButton
                            onClick={() => this.viewIn('/dashboard')}
                        >
                            View stats
                        </TransparentButton>
                    )}
                    {isComparisonEnabled() &&
                        run.baselineRunId &&
                        run.status === RunStatus.COMPLETED && (
                            <TransparentButton
                                onClick={() =>
                                    redirectTo(
                                        SimulationPath.comparison(
                                            run.baselineRunId,
                                            run.id
                                        )
                                    )
                                }
                            >
                                Compare with baseline
                            </TransparentButton>
                        )}
                    {isActiveStatus(run.status) && (
                        <TransparentButton
                            disabled={this.props.acting}
                            onClick={() => this.ask(RunAction.CANCEL)}
                        >
                            Cancel run
                        </TransparentButton>
                    )}
                    {canRunAgain(run) && !interrupted && (
                        <TransparentButton onClick={this.runAgain}>
                            Run again with the same seed
                        </TransparentButton>
                    )}
                    {isTerminalStatus(run.status) && (
                        <TransparentButton
                            disabled={this.props.acting}
                            onClick={() => this.ask(RunAction.DELETE)}
                        >
                            Delete run
                        </TransparentButton>
                    )}
                </PageActionsRow>

                <ConnectionHint
                    connection={this.props.connection}
                    onRetry={this.tryAgain}
                />

                {interrupted && (
                    <div className={'sim-banner warning'}>
                        <i className={'material-icons'}>restart_alt</i>
                        <div>
                            The service restarted while this run was being
                            played. What it produced until then is kept, but the
                            run is not resumed. Play it again from the start
                            with the same seed, or delete it.
                            {canRunAgain(run) && (
                                <BlueContainedButton
                                    text={'Run again with the same seed'}
                                    onClick={this.runAgain}
                                />
                            )}
                        </div>
                    </div>
                )}

                {run.error && (
                    <div className={'sim-banner error'}>
                        <i className={'material-icons'}>error</i>
                        <div>{run.error}</div>
                    </div>
                )}

                <div className={'sim-section'}>
                    <ContentBox
                        title={`Run #${run.id}`}
                        subtitle={run.label}
                        content={this.renderDetails(run)}
                    />
                </div>
                <div className={'sim-section'}>
                    <ContentBox
                        title={'Progress'}
                        content={this.renderProgress(run)}
                    />
                </div>
                {changes && (
                    <div className={'sim-section'}>
                        <ContentBox
                            title={'Rules played with'}
                            tooltipMessage={
                                'This run was played with a snapshot of the rules of the sandbox, with these changes.'
                            }
                            content={<RuleSetChanges changes={changes} />}
                        />
                    </div>
                )}

                <RunResults results={results} scenariosById={scenariosById} />

                <ConfirmModal
                    ref={this.confirmRef}
                    title={description.title}
                    message={description.message}
                    confirmText={description.confirmText}
                    onConfirm={this.confirmPending}
                />
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {
        data,
        results,
        loading,
        notFound,
        acting,
        connection
    } = getRunState(state);

    return {
        run: data,
        results,
        loading,
        notFound,
        acting,
        scenariosById: _.keyBy(getScenariosState(state).data, 'id'),
        connection,
        brandsAndJurisdictions: getBrandsAndJurisdictions(state),
        selectedBrand: getSelectedBrand(state)
    };
};

const mapDispatchToProps = dispatch => ({
    followRun: args => dispatch(followRun(args)),
    stopFollowingRun: () => dispatch(stopFollowingRun()),
    fetchScenarios: () => dispatch(fetchScenarios()),
    cancelRun: args => dispatch(cancelRun(args)),
    deleteRun: args => dispatch(deleteRun(args)),
    setJurisdiction: args => dispatch(setJurisdiction(args)),
    fetchJurisdictions: () =>
        dispatch(fetchJurisdictions({ keepSelection: true }))
});

export default connect(mapStateToProps, mapDispatchToProps)(RunDetailContainer);
